/**
 * Proportional waste-allocation engine (pure, React-free, key-mode agnostic).
 *
 * A primary product and its ingredient-alternatives are ONE interchangeable supply
 * pool. Given each leaf-ingredient demand as a `need` (portions + an ordered option
 * list, primary first) and the period's bought supply per product key, this splits
 * demand across the pool proportionally to remaining capacity, so multi-supplier
 * sourcing of the same ingredient stops manufacturing phantom waste on one product +
 * phantom shortage on another.
 *
 * The engine operates on OPAQUE string keys produced by the caller's `keyOf`
 * (report `productKey` or inventory `inventoryKey`) and OPAQUE per-portion base
 * amounts already in each product's own base unit (g/ml/p) — it never compares
 * units across options, only supply-vs-claims within a single key. Deterministic:
 * no Date/random, fixed iteration order, capped rounds.
 *
 * Singleton pools (no alternatives touch any key in the connected component — the
 * overwhelming majority) SHORT-CIRCUIT to today's exact math (`Σ portions ×
 * perPortionBase`), so results are bit-identical when no alternatives exist anywhere.
 *
 * Frontend twin of server/services/wasteAllocationEngine.ts — keep the two files
 * line-for-line identical in logic (same convention as the cost-calc twins
 * costPriceUtils.ts / dishCostCalc.ts).
 */

/** One option a need can draw from. `perPortionBase` is in THIS key's own base unit. */
export interface NeedOption {
  key: string;
  perPortionBase: number;
}

/**
 * One prep hop the recipe walk took to reach a leaf: `used` of a batch yielding
 * `batchYield`, both in the prep's own `portionsUnit`. `used / batchYield` IS the factor
 * the walk folded into `AllocationNeed.portions`, so a drawer can replay the arithmetic
 * instead of only showing its flattened result.
 */
export interface SubrecipeHop {
  name: string;
  used: number;
  batchYield: number;
  unit: string;
}

/**
 * One leaf-ingredient demand: `portions` (units-sold × any subrecipe/nested factor)
 * of interchangeable supply, drawn from `options` — primary at index 0, then
 * alternatives in declared order. `dishName`/`subrecipes` are attribution labels the
 * engine carries through to the output unchanged (opaque to the allocation math).
 */
export interface AllocationNeed {
  portions: number;
  options: NeedOption[];
  dishName: string;
  subrecipes: string[];
  /** `subrecipes` with the numbers kept — the same hops, in the same dish→inward order.
   *  Attribution only, opaque to the engine (same contract as `subrecipes`), and read
   *  solely by the frontend drawers' prep breakdown: the server twin has no drawer and
   *  deliberately does not carry it. Optional so hand-built needs stay valid. */
  subrecipeHops?: SubrecipeHop[];
}

/** Bought base units (g/ml/p) per product key over the period. */
export type SupplyMap = Map<string, number>;

/**
 * Per-key allocation result. `neededBase` = committed allocation + any shortfall
 * attributed here — a drop-in for today's `NeededInfo.base`. `dishes`/`subrecipes`
 * are the attribution sets (drive the report's "used in dishes" + unmapped check).
 * A key that received demand only as a NON-primary option is flagged
 * `receivedViaAlternative` with the primary key(s) it substitutes for (report badge).
 * `dishesViaAlternative` is the subset of `dishes` whose demand reached this key as a
 * NON-primary option (optIdx > 0) — opaque attribution for the drawer's per-dish
 * alternative marker; it never affects the allocation math.
 */
export interface AllocatedNeed {
  neededBase: number;
  dishes: Set<string>;
  subrecipes: Set<string>;
  receivedViaAlternative: boolean;
  alternativeForKeys: Set<string>;
  dishesViaAlternative: Set<string>;
  /** Base allocated to this key per option-set signature (`optionSetSignature` of the
   *  originating need's options; shortfall lands on the primary's entry). Σ over sets
   *  = `neededBase`. Drives the report's grouped view, which slices a product's
   *  ordered volume across sets proportionally to these — never feeds the math. */
  allocationsBySet: Map<string, number>;
}

export type AllocationResult = Map<string, AllocatedNeed>;

/** Order-insensitive identity of a need's option set (primary/alternative swaps and
 *  duplicate keys collapse). Needs with the same signature — across dishes — belong
 *  to the same grouped-view row. */
export function optionSetSignature(options: NeedOption[]): string {
  return Array.from(new Set(options.map((o) => o.key))).sort().join('');
}

/** Presentation metadata of one option set: distinct member keys (sorted, the
 *  signature's own order), the first-seen primary (group label anchor) and the
 *  attribution labels of every need drawing through the set. */
export interface OptionSetMeta {
  keys: string[];
  primaryKey: string;
  dishes: Set<string>;
  subrecipes: Set<string>;
}

/** Collect per-signature metadata for every option set appearing in `needs`.
 *  Pure presentation data for the grouped report view — no allocation math. */
export function collectOptionSets(needs: AllocationNeed[]): Map<string, OptionSetMeta> {
  const sets = new Map<string, OptionSetMeta>();
  for (const need of needs) {
    if (need.options.length === 0) continue;
    const sig = optionSetSignature(need.options);
    let meta = sets.get(sig);
    if (!meta) {
      meta = {
        keys: Array.from(new Set(need.options.map((o) => o.key))).sort(),
        primaryKey: need.options[0].key,
        dishes: new Set(),
        subrecipes: new Set(),
      };
      sets.set(sig, meta);
    }
    meta.dishes.add(need.dishName);
    for (const s of need.subrecipes) meta.subrecipes.add(s);
  }
  return sets;
}

/** Partition every key appearing in `needs` into its interchangeable-supply pool —
 *  the same union-find the allocator builds (each need links primary↔alternatives;
 *  a single-option need joins whatever pool its key already belongs to). Returns
 *  canonical pool id → sorted member keys. The id is the lexicographically smallest
 *  member key — stable across periods and iteration order, unlike the raw union-find
 *  root. Presentation metadata for the report's pooled view — no allocation math. */
export function collectPools(needs: AllocationNeed[]): Map<string, string[]> {
  const parent = new Map<string, string>();
  for (const need of needs) {
    if (need.options.length === 0) continue;
    for (const opt of need.options) ufFind(parent, opt.key);
    for (let i = 1; i < need.options.length; i++) ufUnion(parent, need.options[0].key, need.options[i].key);
  }
  const membersByRoot = new Map<string, string[]>();
  for (const key of Array.from(parent.keys())) {
    const root = ufFind(parent, key);
    const arr = membersByRoot.get(root);
    if (arr) arr.push(key);
    else membersByRoot.set(root, [key]);
  }
  const pools = new Map<string, string[]>();
  for (const members of Array.from(membersByRoot.values())) {
    members.sort();
    pools.set(members[0], members);
  }
  return pools;
}

/** Max distribute→scale-down→commit iterations before attributing the rest to primaries. */
const MAX_ROUNDS = 10;
/** Portions/base below this are treated as fully met / converged. */
const EPSILON = 1e-9;

function addSetAllocation(e: AllocatedNeed, sig: string, base: number): void {
  if (base <= 0) return;
  e.allocationsBySet.set(sig, (e.allocationsBySet.get(sig) ?? 0) + base);
}

// --- union-find over product keys -----------------------------------------

function ufFind(parent: Map<string, string>, x: string): string {
  if (!parent.has(x)) {
    parent.set(x, x);
    return x;
  }
  let root = x;
  while (parent.get(root) !== root) root = parent.get(root) as string;
  let cur = x;
  while (parent.get(cur) !== root) {
    const next = parent.get(cur) as string;
    parent.set(cur, root);
    cur = next;
  }
  return root;
}

function ufUnion(parent: Map<string, string>, a: string, b: string): void {
  const ra = ufFind(parent, a);
  const rb = ufFind(parent, b);
  if (ra !== rb) parent.set(ra, rb);
}

// --- engine ---------------------------------------------------------------

/**
 * Allocate `needs` against `supply`. Returns per-key `neededBase` + attribution.
 * Keys never touched by any need (or with zero allocation and no shortfall) are
 * simply absent from the result. The engine never mutates `supply`.
 */
export function allocateNeeds(needs: AllocationNeed[], supply: SupplyMap): AllocationResult {
  // Group keys connected through any need's option list (a need with alternatives
  // links primary + alts). Single-option needs link nothing but still belong to the
  // component of their key (so a single-option demand on a key that another need
  // lists as an option competes inside that pool — Case B).
  const parent = new Map<string, string>();
  for (const need of needs) {
    if (need.options.length === 0) continue;
    for (const opt of need.options) ufFind(parent, opt.key);
    for (let i = 1; i < need.options.length; i++) ufUnion(parent, need.options[0].key, need.options[i].key);
  }

  const needsByRoot = new Map<string, AllocationNeed[]>();
  for (const need of needs) {
    if (need.options.length === 0) continue;
    const root = ufFind(parent, need.options[0].key);
    const arr = needsByRoot.get(root);
    if (arr) arr.push(need);
    else needsByRoot.set(root, [need]);
  }

  const result: AllocationResult = new Map();
  const ensure = (key: string): AllocatedNeed => {
    let e = result.get(key);
    if (!e) {
      e = { neededBase: 0, dishes: new Set(), subrecipes: new Set(), receivedViaAlternative: false, alternativeForKeys: new Set(), dishesViaAlternative: new Set(), allocationsBySet: new Map() };
      result.set(key, e);
    }
    return e;
  };

  for (const groupNeeds of Array.from(needsByRoot.values())) {
    const hasAlternatives = groupNeeds.some((n) => n.options.length > 1);
    if (!hasAlternatives) {
      // SHORT-CIRCUIT (today's math, bit-identical): needed = Σ portions × perPortionBase.
      // Accumulation order per key matches the caller's walk order → no float drift.
      for (const need of groupNeeds) {
        const opt = need.options[0];
        const e = ensure(opt.key);
        e.neededBase += need.portions * opt.perPortionBase;
        addSetAllocation(e, optionSetSignature(need.options), need.portions * opt.perPortionBase);
        e.dishes.add(need.dishName);
        for (const s of need.subrecipes) e.subrecipes.add(s);
      }
      continue;
    }
    allocateGroup(groupNeeds, supply, ensure);
  }

  return result;
}

/** Iterative proportional allocation within one pool (≥1 need has alternatives). */
function allocateGroup(groupNeeds: AllocationNeed[], supply: SupplyMap, ensure: (key: string) => AllocatedNeed): void {
  const needSigs = groupNeeds.map((n) => optionSetSignature(n.options));
  const remaining = new Map<string, number>();
  for (const need of groupNeeds) {
    for (const opt of need.options) {
      if (!remaining.has(opt.key)) remaining.set(opt.key, Math.max(0, supply.get(opt.key) ?? 0));
    }
  }

  // Every PURCHASED pool member is a mapped row even if allocation lands 0 on it: a
  // bought alternative that ends up unused is genuine waste, not "purchased but not
  // linked". Seed its attribution (+ alternative badge) up front.
  for (const need of groupNeeds) {
    for (let i = 0; i < need.options.length; i++) {
      const opt = need.options[i];
      if ((remaining.get(opt.key) ?? 0) <= 0) continue;
      const e = ensure(opt.key);
      e.dishes.add(need.dishName);
      for (const s of need.subrecipes) e.subrecipes.add(s);
      if (i > 0) {
        e.receivedViaAlternative = true;
        e.alternativeForKeys.add(need.options[0].key);
        e.dishesViaAlternative.add(need.dishName);
      }
    }
  }

  const unmet = groupNeeds.map((n) => n.portions);

  for (let round = 0; round < MAX_ROUNDS; round++) {
    // 1. Distribute: each unmet need spreads its portions across options
    //    proportionally to remaining capacity (remainingSupply / perPortionBase).
    const claimByKey = new Map<string, number>();
    const claims: Array<{ needIdx: number; optIdx: number; key: string; portions: number; base: number }> = [];
    for (let ni = 0; ni < groupNeeds.length; ni++) {
      if (unmet[ni] <= EPSILON) continue;
      const options = groupNeeds[ni].options;
      let totalWeight = 0;
      const weights = options.map((opt) => {
        const rem = remaining.get(opt.key) ?? 0;
        const w = opt.perPortionBase > 0 && rem > 0 ? rem / opt.perPortionBase : 0;
        totalWeight += w;
        return w;
      });
      if (totalWeight <= 0) continue; // no option has supply left → stays unmet (→ shortfall)
      for (let oi = 0; oi < options.length; oi++) {
        if (weights[oi] <= 0) continue;
        const portions = unmet[ni] * (weights[oi] / totalWeight);
        const base = portions * options[oi].perPortionBase;
        claims.push({ needIdx: ni, optIdx: oi, key: options[oi].key, portions, base });
        claimByKey.set(options[oi].key, (claimByKey.get(options[oi].key) ?? 0) + base);
      }
    }
    if (claims.length === 0) break;

    // 2. Scale down: on each oversubscribed key, scale ALL its claims by
    //    remaining / totalClaims (dishes share it proportionally to their demand).
    // 3. Commit scaled allocations and subtract supply.
    let committedThisRound = 0;
    for (const c of claims) {
      const totalClaim = claimByKey.get(c.key) ?? 0;
      const rem = remaining.get(c.key) ?? 0;
      const factor = totalClaim > rem && totalClaim > 0 ? rem / totalClaim : 1;
      const committedBase = c.base * factor;
      if (committedBase <= 0) continue;
      const e = ensure(c.key);
      e.neededBase += committedBase;
      addSetAllocation(e, needSigs[c.needIdx], committedBase);
      e.dishes.add(groupNeeds[c.needIdx].dishName);
      for (const s of groupNeeds[c.needIdx].subrecipes) e.subrecipes.add(s);
      if (c.optIdx > 0) {
        e.receivedViaAlternative = true;
        e.alternativeForKeys.add(groupNeeds[c.needIdx].options[0].key);
        e.dishesViaAlternative.add(groupNeeds[c.needIdx].dishName);
      }
      unmet[c.needIdx] -= c.portions * factor;
      committedThisRound += committedBase;
    }
    for (const [key, totalClaim] of Array.from(claimByKey.entries())) {
      const rem = remaining.get(key) ?? 0;
      const factor = totalClaim > rem && totalClaim > 0 ? rem / totalClaim : 1;
      remaining.set(key, Math.max(0, rem - totalClaim * factor));
    }
    if (committedThisRound <= EPSILON) break; // converged
  }

  // 4. Shortfall: portions with no supply left anywhere in their option list are
  //    attributed to the PRIMARY at its perPortionBase (preserves today's
  //    under-ordered signal on the primary's row).
  for (let ni = 0; ni < groupNeeds.length; ni++) {
    if (unmet[ni] <= EPSILON) continue;
    const primary = groupNeeds[ni].options[0];
    const e = ensure(primary.key);
    e.neededBase += unmet[ni] * primary.perPortionBase;
    addSetAllocation(e, needSigs[ni], unmet[ni] * primary.perPortionBase);
    e.dishes.add(groupNeeds[ni].dishName);
    for (const s of groupNeeds[ni].subrecipes) e.subrecipes.add(s);
  }
}
